import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Table, 
  DatePicker, 
  Select, 
  Button, 
  Space, 
  Divider,
  List,
  Statistic,
  Spin,
  Alert
} from 'antd';
import { 
  FilterOutlined,
  CalendarOutlined,
  HomeOutlined,
  UserOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosInstance';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ReservationReportsPage = () => {
  const { authData } = useAuth();
  const [statistics, setStatistics] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState({
    stats: false,
    timeline: false,
    rooms: false
  });
  const [roomId, setRoomId] = useState('all');
  const [dateRange, setDateRange] = useState([]);

  const hotelId = authData?.hotelId;

  const fetchRooms = async () => {
    if (!hotelId) return;
    setLoading(prev => ({...prev, rooms: true}));
    try {
      const response = await axiosInstance.get(`/Rooms/all/${hotelId}`);
      setRooms(response.data);
    } catch (error) {
      console.error("Error fetching rooms:", error);
    } finally {
      setLoading(prev => ({...prev, rooms: false}));
    }
  };

  const fetchStatistics = async () => {
    if (!hotelId) return;
    setLoading(prev => ({...prev, stats: true}));
    try {
      const params = {
        hotelId,
        ...(roomId !== 'all' && { roomId }),
        ...(dateRange[0] && { startDate: dateRange[0].toISOString() }),
        ...(dateRange[1] && { endDate: dateRange[1].toISOString() }),
      };
      const response = await axiosInstance.get("/RoomStatistics", { params });
      setStatistics(response.data);
    } catch (error) {
      console.error("Error fetching statistics:", error);
    } finally {
      setLoading(prev => ({...prev, stats: false}));
    }
  };

  const fetchTimeline = async () => {
    if (!hotelId) return;
    setLoading(prev => ({...prev, timeline: true}));
    try {
      const params = {
        hotelId,
        ...(roomId !== 'all' && { roomId }),
        ...(dateRange[0] && { startDate: dateRange[0].toISOString() }),
        ...(dateRange[1] && { endDate: dateRange[1].toISOString() }),
      };
      const response = await axiosInstance.get("/RoomStatistics/timeline", { params });
      setTimeline(response.data);
    } catch (error) {
      console.error("Error fetching timeline:", error);
    } finally {
      setLoading(prev => ({...prev, timeline: false}));
    }
  };

  useEffect(() => {
    if (hotelId) {
      fetchRooms();
      fetchStatistics();
      fetchTimeline();
    }
  }, [hotelId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchStatistics();
    fetchTimeline();
  };

  const columns = [
    {
      title: 'Room Number',
      dataIndex: 'roomNumber',
      key: 'roomNumber',
    },
    {
      title: 'Room Type',
      dataIndex: 'roomType',
       key: 'roomType',
  render: typeName => typeName || 'Unknown', // This shows "Unknown" when typeName is falsy
    },
    {
      title: 'Guest Name',
      dataIndex: 'guestName',
      key: 'guestName',
    },
    {
      title: 'Check-In',
      dataIndex: 'checkInDate',
      key: 'checkInDate',
      render: date => date ? moment(date).format('LL') : 'N/A'
    },
    {
      title: 'Check-Out',
      dataIndex: 'checkOutDate',
      key: 'checkOutDate',
      render: date => date ? moment(date).format('LL') : 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'bookingStatus',
      key: 'bookingStatus',
    },
    {
      title: 'Booking Date',
      dataIndex: 'bookingMadeOn',
      key: 'bookingMadeOn',
      render: date => moment(date).format('LL')
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <LineChartOutlined />
            <Title level={4} style={{ margin: 0 }}>Reservation Reports</Title>
          </Space>
        }
        bordered={false}
      >
        <Card
          type="inner"
          title="Filters"
          style={{ marginBottom: '24px' }}
        >
          <form onSubmit={handleSubmit}>
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Text strong>Room:</Text>
                <Select
                  style={{ width: '100%' }}
                  value={roomId}
                  onChange={setRoomId}
                  loading={loading.rooms}
                  suffixIcon={<HomeOutlined />}
                >
                  <Option value="all">All Rooms</Option>
                  {rooms.map(room => (
                    <Option key={room.roomId} value={room.roomId}>
                      {room.roomNumber} - {room.roomTypeName  || "Unknown rr"}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>Date Range:</Text>
                <RangePicker
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={setDateRange}
                  suffixIcon={<CalendarOutlined />}
                />
              </Col>
              <Col xs={24} md={4}>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<FilterOutlined />}
                  loading={loading.stats || loading.timeline}
                  style={{ width: '100%', marginTop: '30px' }}
                >
                  Apply Filters
                </Button>
              </Col>
            </Row>
          </form>
        </Card>

        {/* Statistics Section */}
        <Card
          title="Booking Statistics"
          style={{ marginBottom: '24px' }}
          loading={loading.stats}
        >
          {statistics ? (
            <>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Bookings"
                    value={statistics.totalBookings}
                    prefix={<UserOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Checked-In"
                    value={statistics.occupiedBookings}
                    prefix={<HomeOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Occupancy Rate"
                    value={statistics.occupancyRate}
                    precision={2}
                    suffix="%"
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Avg Duration"
                    value={statistics.averageBookingDurationDays}
                    precision={2}
                    suffix="days"
                  />
                </Col>
              </Row>

              <Divider />

              <Title level={5}>Detailed Bookings</Title>
              <Table
                columns={columns}
                dataSource={statistics.bookings}
                rowKey={(record, index) => index}
                pagination={{ pageSize: 10 }}
                scroll={{ x: true }}
              />
            </>
          ) : (
            <Alert
              message="No statistics available"
              description="Please apply filters to view booking statistics"
              type="info"
              showIcon
            />
          )}
        </Card>

        {/* Timeline Section */}
        <Card
          title="Occupancy Timeline"
          loading={loading.timeline}
        >
          {timeline && timeline.length > 0 ? (
            <List
              itemLayout="vertical"
              dataSource={timeline}
              renderItem={day => (
                <List.Item key={day.date}>
                  <List.Item.Meta
                    title={moment(day.date).format('LL')}
                  />
                  {day.bookings && day.bookings.length > 0 ? (
                    <List
                      size="small"
                      dataSource={day.bookings}
                      renderItem={booking => (
                        <List.Item>
                          <Space direction="vertical">
                            <Text>
                              Room {booking.roomNumber} occupied by {booking.guestName}
                            </Text>
                            <Text type="secondary">
                              Check-in: {moment(booking.checkInDate).format('LL')}
                              {booking.checkOutDate && ` • Check-out: ${moment(booking.checkOutDate).format('LL')}`}
                            </Text>
                            <Text>Status: {booking.bookingStatus}</Text>
                          </Space>
                        </List.Item>
                      )}
                    />
                  ) : (
                    <Text type="secondary">No occupancy for this day</Text>
                  )}
                </List.Item>
              )}
            />
          ) : (
            <Alert
              message="No timeline data available"
              description="Please apply filters to view occupancy timeline"
              type="info"
              showIcon
            />
          )}
        </Card>
      </Card>
    </div>
  );
};

export default ReservationReportsPage;